/**
 * jsonMergeUtil_Conversion.ts - JSON merge helpers for combining template, config, and context JSON.
 * Port of C# JsonMergeUtil_Conversion.cs.
 *
 * Builds the merged working JsonObject by combining templateJson with optional configJson and
 * contextJson overrides. Arrays are kept as native JsonArray values (no dict flattening) so
 * downstream helpers iterate the JsonArray directly.
 *
 * Precedence: contextJson > configJson (with AllowedRoles check) > templateJson
 */

import { JsonValue } from '../../app/json/jsonValue.js';
import { JsonArray } from '../../app/json/jsonArray.js';
import { JsonObject } from '../../app/json/jsonObject.js';
import { canOverrideKey } from './jsonMergeUtil_OverridePermissions.js';
import { tryGetComparableScalar } from './jsonMergeUtil_Scalar.js';

/**
 * Builds the merged working JsonObject by combining templateJson with optional
 * configJson and contextJson overrides. Mirrors csharp BuildMergedJson.
 *
 * IMMUTABILITY: contextJson and configJson are treated as read-only here. Values are
 * only READ and copied into the output `merged` JsonObject. When contextJson smart-merges
 * into an existing array, the merged JsonObject owns a fresh JsonArray clone so the original
 * templateJson array is never mutated.
 */
export function buildMergedJson(
    templateJson: JsonObject,
    contextJson: JsonObject | null = null,
    configJson: JsonObject | null = null
): JsonObject {
    const merged = new JsonObject();

    // Step 1: Add all template JSON keys (lowest priority).
    // Arrays are cloned so contextJson smart-merge can append items without mutating templateJson.
    for (const [key, value] of templateJson.entries()) {
        merged.set(key, value.isArray ? cloneArrayValue(value) : value);
    }

    // Step 2: Apply configJson overrides (medium priority).
    // ONLY for keys that exist in templateJson AND have AllowedRoles permission.
    if (configJson != null) {
        for (const [key, value] of configJson.entries()) {
            if (canOverrideKey(key, templateJson, contextJson)) {
                merged.set(key, value.isArray ? cloneArrayValue(value) : value);
            }
        }
    }

    // Step 3: Apply contextJson overrides (highest priority - always wins).
    // Arrays: overwrite by default, but smart merge (append/skip) if ArrayNamePrimary found in contextJson.
    if (contextJson != null) {
        for (const [key, value] of contextJson.entries()) {
            if (value.isArray) {
                const contextArray = value.asArray();
                if (contextArray == null) continue;

                const primaryKeyName = key + 'Primary';
                const primaryLookup = contextJson.tryGetValue(primaryKeyName);
                const existingLookup = merged.tryGetValue(key);
                if (primaryLookup.found && primaryLookup.value.isString
                    && existingLookup.found && existingLookup.value.isArray) {
                    // Smart merge: append context items not already in existing array.
                    const existingArray = existingLookup.value.getArray();
                    const primaryKey = primaryLookup.value.asString()!;
                    for (let g = 0; g < contextArray.length; g++) {
                        const contextItem = contextArray.get(g);
                        if (!contextItem.isObject) continue;
                        const contextObj = contextItem.asObject();
                        if (contextObj == null) continue;

                        let existsInTemplate = false;
                        for (let c = 0; c < existingArray.length; c++) {
                            const existingItem = existingArray.get(c);
                            if (!existingItem.isObject) continue;
                            const existingObj = existingItem.asObject();
                            if (existingObj == null) continue;
                            if (matchesByStringKeys(existingObj, contextObj, primaryKey)) {
                                existsInTemplate = true;
                                break;
                            }
                        }
                        if (!existsInTemplate) {
                            existingArray.add(contextItem);
                        }
                    }
                } else {
                    // Default: context arrays replace any lower-priority values.
                    merged.set(key, cloneArrayValue(value));
                }
            } else {
                merged.set(key, value);
            }
        }
    }

    return merged;
}

// Clones the JsonArray inside a JsonValue.Array so smart-merge appends do not mutate the
// caller's source. Items themselves are not deep-cloned — JsonValue scalars are immutable,
// and shared object/array references are read-only via IsReadOnly propagation.
function cloneArrayValue(value: JsonValue): JsonValue {
    const source = value.asArray();
    if (source == null) return value;
    const copy = new JsonArray();
    for (let i = 0; i < source.length; i++) {
        copy.add(source.get(i));
    }
    return JsonValue.array(copy);
}

/**
 * Determines whether two array-item JsonObjects match by primary key or shared scalar string values.
 * Scalar equality uses case-insensitive comparison to match the assembler's case-insensitive lookup.
 */
function matchesByStringKeys(
    templateItem: JsonObject,
    contextItem: JsonObject,
    primaryKey: string | null
): boolean {
    if (primaryKey !== null) {
        const tLookup = templateItem.tryGetValue(primaryKey);
        const cLookup = contextItem.tryGetValue(primaryKey);
        if (!tLookup.found || !cLookup.found) return false;
        const tScalar = tryGetComparableScalar(tLookup.value);
        const cScalar = tryGetComparableScalar(cLookup.value);
        if (!tScalar.found || !cScalar.found) return false;
        return tScalar.result.toLowerCase() === cScalar.result.toLowerCase();
    }

    let hasStringKey = false;
    for (const [k, v] of contextItem.entries()) {
        const cScalar = tryGetComparableScalar(v);
        if (!cScalar.found) continue;
        hasStringKey = true;
        const tLookup = templateItem.tryGetValue(k);
        if (!tLookup.found) return false;
        const tScalar = tryGetComparableScalar(tLookup.value);
        if (!tScalar.found) return false;
        if (tScalar.result.toLowerCase() !== cScalar.result.toLowerCase()) return false;
    }
    return hasStringKey;
}
